import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Phone, Clock, Star, Calendar, Mail, Globe, Coins, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dealer } from "@/types/dealer";
import { formatCurrency } from "@/lib/utils";
import { calculateDealerPrice, calculateGoldPrice, calculateSilverPrice } from "@/lib/pricing";
import { DEFAULT_GOLD_PURITY, DEFAULT_SILVER_PURITY } from "@/lib/constants";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { BadgeCheck, Clock as ClockIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { activationRpcService } from "@/services/activationRpcService";
import { useStore } from "@/store/useStore";
import { supabase } from "@/lib/supabase";

interface DealerInfoCardProps {
  dealer: Dealer;
  goldPrice: any | null;
  silverPrice: any | null;
  isAuthenticated: boolean;
  handleActivateDealer: () => void;
  onMetalCardClick?: (metal: 'gold' | 'silver') => void;
  userDistance?: string | null;
}

export function DealerInfoCard({
  dealer,
  goldPrice,
  silverPrice,
  isAuthenticated,
  handleActivateDealer,
  onMetalCardClick,
  userDistance
}: DealerInfoCardProps) {
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [isCheckingRequest, setIsCheckingRequest] = useState(true);
  
  // Utilizzo dello store centralizzato come nella card blocca prezzo
  const {
    currentGoldPrice: rawGoldPrice,
    currentSilverPrice: rawSilverPrice,
  } = useStore();

  // Stati per i prezzi calcolati
  const [goldBasePrice, setGoldBasePrice] = useState(0);
  const [silverBasePrice, setSilverBasePrice] = useState(0);
  const [goldDealerPrice, setGoldDealerPrice] = useState(0);
  const [silverDealerPrice, setSilverDealerPrice] = useState(0);
  const [goldSpread, setGoldSpread] = useState<number | null>(null);
  const [silverSpread, setSilverSpread] = useState<number | null>(null);

  // Funzione per recuperare lo spread dal database (come in usePriceLock)
  const fetchCurrentSpread = async (metal: 'ORO' | 'ARGENTO', purity: string) => {
    try {
      console.log(`[DealerInfoCard] Fetching spread for ${metal} ${purity}`);
      
      // Normalizza la purezza per la query (rimuove spazi)
      const normalizedPurity = String(purity).replace(/\s+/g, '');
      
      console.log(`[DealerInfoCard] Normalized purity for query: "${normalizedPurity}"`);
      
      const { data, error } = await supabase
        .from('vw_active_quotations')
        .select('spread_type, spread_percentage, spread_fixed')
        .eq('dealer_id', dealer.id)
        .eq('metal', metal)
        .eq('purity', normalizedPurity)
        .single();
      
      if (error) {
        console.error('[DealerInfoCard] Error fetching spread:', error);
        console.log('[DealerInfoCard] Query parameters:', { dealerId: dealer.id, metal, purity: normalizedPurity });
        return dealer.buySpread;
      }
      
      if (data) {
        console.log('[DealerInfoCard] Fetched quotation:', data);
        if (data.spread_type === 'PERCENTAGE' && data.spread_percentage !== null) {
          return data.spread_percentage;
        }
      }
      
      return dealer.buySpread;
    } catch (error) {
      console.error('[DealerInfoCard] Error in fetchCurrentSpread:', error);
      return dealer.buySpread;
    }
  };

  // Funzione per aggiornare i prezzi (come in usePriceLock)
  const updatePrices = async () => {
    if (!dealer.activated) return;

    let currentGoldBasePrice = 0;
    let currentSilverBasePrice = 0;
    
    // Calcola prezzi base usando le stesse funzioni della card blocca prezzo
    if (rawGoldPrice) {
      currentGoldBasePrice = calculateGoldPrice(rawGoldPrice, DEFAULT_GOLD_PURITY);
      console.log(`[DealerInfoCard] Calculated gold base price: ${currentGoldBasePrice} for purity ${DEFAULT_GOLD_PURITY}`);
    }
    
    if (rawSilverPrice) {
      currentSilverBasePrice = calculateSilverPrice(rawSilverPrice, DEFAULT_SILVER_PURITY, 'g');
      console.log(`[DealerInfoCard] Calculated silver base price: ${currentSilverBasePrice} for purity ${DEFAULT_SILVER_PURITY}`);
    }
    
    setGoldBasePrice(currentGoldBasePrice);
    setSilverBasePrice(currentSilverBasePrice);
    
    // Recupera gli spread dal database per ogni metallo
    const latestGoldSpread = await fetchCurrentSpread('ORO', DEFAULT_GOLD_PURITY);
    const latestSilverSpread = await fetchCurrentSpread('ARGENTO', DEFAULT_SILVER_PURITY);
    
    setGoldSpread(latestGoldSpread === undefined ? null : latestGoldSpread);
    setSilverSpread(latestSilverSpread === undefined ? null : latestSilverSpread);
    
    console.log(`[DealerInfoCard] Using gold spread: ${latestGoldSpread}, silver spread: ${latestSilverSpread}`);
    
    // Calcola prezzi dealer con spread
    if (latestGoldSpread !== null && latestGoldSpread !== undefined && currentGoldBasePrice > 0) {
      const calculatedGoldDealerPrice = calculateDealerPrice(currentGoldBasePrice, latestGoldSpread);
      setGoldDealerPrice(calculatedGoldDealerPrice || currentGoldBasePrice);
      console.log(`[DealerInfoCard] Calculated gold dealer price with spread ${latestGoldSpread}: ${calculatedGoldDealerPrice}`);
    } else {
      setGoldDealerPrice(0);
      console.log(`[DealerInfoCard] No gold spread available, dealer price set to 0`);
    }
    
    if (latestSilverSpread !== null && latestSilverSpread !== undefined && currentSilverBasePrice > 0) {
      const calculatedSilverDealerPrice = calculateDealerPrice(currentSilverBasePrice, latestSilverSpread);
      setSilverDealerPrice(calculatedSilverDealerPrice || currentSilverBasePrice);
      console.log(`[DealerInfoCard] Calculated silver dealer price with spread ${latestSilverSpread}: ${calculatedSilverDealerPrice}`);
    } else {
      setSilverDealerPrice(0);
      console.log(`[DealerInfoCard] No silver spread available, dealer price set to 0`);
    }
  };

  useEffect(() => {
    if (!dealer.activated) {
      setIsCheckingRequest(true);
      activationRpcService.checkPendingRequest(dealer.id)
        .then(hasPending => {
          setHasPendingRequest(hasPending);
        })
        .finally(() => {
          setIsCheckingRequest(false);
        });
    }
  }, [dealer.id, dealer.activated]);

  // Effetto per aggiornare i prezzi quando cambiano i dati
  useEffect(() => {
    console.log(`[DealerInfoCard] useEffect triggered. Updating prices.`);
    updatePrices();
  }, [dealer.id, dealer.activated, rawGoldPrice, rawSilverPrice]);
  
  const rating = typeof dealer.rating === 'number' ? dealer.rating : 0;
  const absGoldSpread = typeof goldSpread === 'number' ? Math.abs(goldSpread) : null;
  const absSilverSpread = typeof silverSpread === 'number' ? Math.abs(silverSpread) : null;
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-xl md:text-2xl">{dealer.name}</CardTitle>
          {userDistance && (
            <p className="text-sm text-muted-foreground mt-1">
              <MapPin className="h-3 w-3 inline mr-1" />
              Distanza: {userDistance}
            </p>
          )}
          {dealer.is_verified && (
            <Badge variant="outline" className="mt-2">Verificato</Badge>
          )}
        </div>
        
        {!dealer.activated && isAuthenticated && typeof handleActivateDealer === 'function' && (
          <Button 
            onClick={(e) => {
              e.preventDefault();
              handleActivateDealer();
            }} 
            size="sm" 
            variant="outline"
            disabled={hasPendingRequest || isCheckingRequest}
          >
            {isCheckingRequest ? (
              <>
                <ClockIcon className="mr-2 h-4 w-4 animate-spin" />
                Verifica...
              </>
            ) : hasPendingRequest ? (
              <>
                <ClockIcon className="mr-2 h-4 w-4" />
                In attesa
              </>
            ) : (
              "Attiva Attività"
            )}
          </Button>
        )}
      </CardHeader>
      
      <CardContent className="grid gap-6">
        {!dealer.activated && (
          <Alert className="bg-amber-50 border-amber-200 text-amber-800">
            {hasPendingRequest ? (
              <>
                <ClockIcon className="h-4 w-4" />
                <AlertDescription>
                  Questa attività ha una richiesta di attivazione in corso. La richiesta è in attesa di verifica da parte dell'amministratore.
                </AlertDescription>
              </>
            ) : (
              <>
                <BadgeCheck className="h-4 w-4" />
                <AlertDescription>
                  Questa attività non è ancora attivata. 
                  {isAuthenticated 
                    ? " Puoi richiedere l'attivazione cliccando sul pulsante 'Attiva Attività'." 
                    : " Accedi per richiedere l'attivazione di questa attività."}
                </AlertDescription>
              </>
            )}
          </Alert>
        )}
        
        {dealer.activated === true && (
          <div className="space-y-6">
            {/* Container per le card affiancate */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Card Quotazioni Oro */}
              <div 
                className="relative overflow-hidden rounded-xl border border-amber-200/50 bg-gradient-to-br from-amber-50/80 via-yellow-50/60 to-orange-50/40 p-6 shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02] dark:from-amber-950/20 dark:via-yellow-950/15 dark:to-orange-950/10 dark:border-amber-800/30 cursor-pointer"
                onClick={() => onMetalCardClick?.('gold')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onMetalCardClick?.('gold');
                  }
                }}
              >
                {/* Icona e header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 shadow-md">
                    <Coins className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-amber-900 dark:text-amber-100">Oro</h3>
                    <p className="text-sm text-amber-700/80 dark:text-amber-300/80">24kt</p>
                  </div>
                </div>

                {/* Prezzi */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-amber-700/90 dark:text-amber-300/90">Prezzo Mercato</p>
                      <p className="text-xl font-bold text-amber-900 dark:text-amber-100">
                        {formatCurrency(goldBasePrice)}<span className="text-sm font-normal">/g</span>
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-amber-700/90 dark:text-amber-300/90">Prezzo Acquisto</p>
                      {goldDealerPrice > 0 ? (
                        <p className="text-xl font-bold text-amber-800 dark:text-amber-200">
                          {formatCurrency(goldDealerPrice)}<span className="text-sm font-normal">/g</span>
                        </p>
                      ) : (
                        <p className="text-lg font-medium text-amber-600/70 dark:text-amber-400/70 italic">
                          Non disponibile
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Spread badge */}
                  {typeof goldSpread === 'number' && goldDealerPrice > 0 && (
                    <div className="flex items-center justify-center">
                      <div className="inline-flex items-center gap-2 rounded-full bg-amber-100/80 dark:bg-amber-900/30 px-3 py-1.5 border border-amber-200/50 dark:border-amber-800/50">
                        <Zap className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                        <span className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                          Spread -{absGoldSpread}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Pattern decorativo */}
                <div className="absolute -top-4 -right-4 h-16 w-16 rounded-full bg-gradient-to-br from-amber-300/20 to-yellow-400/20 blur-xl"></div>
                <div className="absolute -bottom-2 -left-2 h-12 w-12 rounded-full bg-gradient-to-br from-orange-300/20 to-amber-400/20 blur-lg"></div>
              </div>

              {/* Card Quotazioni Argento */}
              <div 
                className="relative overflow-hidden rounded-xl border border-slate-200/50 bg-gradient-to-br from-slate-50/80 via-gray-50/60 to-zinc-50/40 p-6 shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02] dark:from-slate-950/20 dark:via-gray-950/15 dark:to-zinc-950/10 dark:border-slate-700/30 cursor-pointer"
                onClick={() => onMetalCardClick?.('silver')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onMetalCardClick?.('silver');
                  }
                }}
              >
                {/* Icona e header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-slate-400 to-gray-500 shadow-md">
                    <Coins className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-slate-900 dark:text-slate-100">Argento</h3>
                    <p className="text-sm text-slate-700/80 dark:text-slate-300/80">999‰</p>
                  </div>
                </div>

                {/* Prezzi */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-slate-700/90 dark:text-slate-300/90">Prezzo Mercato</p>
                      <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
                        {formatCurrency(silverBasePrice)}<span className="text-sm font-normal">/g</span>
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-slate-700/90 dark:text-slate-300/90">Prezzo Acquisto</p>
                      {silverDealerPrice > 0 ? (
                        <p className="text-xl font-bold text-slate-800 dark:text-slate-200">
                          {formatCurrency(silverDealerPrice)}<span className="text-sm font-normal">/g</span>
                        </p>
                      ) : (
                        <p className="text-lg font-medium text-slate-600/70 dark:text-slate-400/70 italic">
                          Non disponibile
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Spread badge */}
                  {typeof silverSpread === 'number' && silverDealerPrice > 0 && (
                    <div className="flex items-center justify-center">
                      <div className="inline-flex items-center gap-2 rounded-full bg-slate-100/80 dark:bg-slate-900/30 px-3 py-1.5 border border-slate-200/50 dark:border-slate-700/50">
                        <Zap className="h-3 w-3 text-slate-600 dark:text-slate-400" />
                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                          Spread -{absSilverSpread}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Pattern decorativo */}
                <div className="absolute -top-4 -right-4 h-16 w-16 rounded-full bg-gradient-to-br from-slate-300/20 to-gray-400/20 blur-xl"></div>
                <div className="absolute -bottom-2 -left-2 h-12 w-12 rounded-full bg-gradient-to-br from-zinc-300/20 to-slate-400/20 blur-lg"></div>
              </div>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-2">
            <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="font-medium">Indirizzo</p>
              <p className="text-sm text-muted-foreground">{dealer.address}</p>
              {(dealer.city || dealer.postal_code) && (
                <p className="text-sm text-muted-foreground">
                  {dealer.city}{dealer.postal_code ? ` - ${dealer.postal_code}` : ''}
                </p>
              )}
            </div>
          </div>
          
          {dealer.phone && (
            <div className="flex items-start gap-2">
              <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium">Telefono</p>
                <a 
                  href={`tel:${dealer.phone}`}
                  className="text-sm font-medium text-gold-dark dark:text-gold-light hover:text-gold-light dark:hover:text-gold-dark transition-colors duration-200 underline decoration-gold-dark/30 hover:decoration-gold-dark dark:decoration-gold-light/30 dark:hover:decoration-gold-light underline-offset-2"
                >
                  {dealer.phone}
                </a>
              </div>
            </div>
          )}
          
          {dealer.email && (
            <div className="flex items-start gap-2">
              <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium">Email</p>
                <p className="text-sm text-muted-foreground">{dealer.email}</p>
              </div>
            </div>
          )}
          
          
          {dealer.opening_hours && (
            <div className="flex items-start gap-2 col-span-1 md:col-span-2">
              <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium">Orari di Apertura</p>
                <p className="text-sm text-muted-foreground whitespace-pre-line">{dealer.opening_hours}</p>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-1 pt-2 border-t">
          <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
          <span className="font-medium">{rating.toFixed(1)}</span>
          <span className="text-muted-foreground">/ 5</span>
        </div>
      </CardContent>
    </Card>
  );
}
